import React, { createContext, useContext, useCallback, useState, ReactNode } from 'react';

type NotificationVariant = 'success' | 'info' | 'warning' | 'error';

export interface Notification {
  id: string;
  message: string;
  variant: NotificationVariant;
  timeoutMs?: number;
}

interface NotificationContextType {
  notify: (message: string, options?: { variant?: NotificationVariant; timeoutMs?: number }) => void;
  remove: (id: string) => void;
  notifications: Notification[];
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = (): NotificationContextType => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationProvider');
  return ctx;
};

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const remove = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const notify = useCallback((message: string, options?: { variant?: NotificationVariant; timeoutMs?: number }) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const variant: NotificationVariant = options?.variant ?? 'info';
    const timeoutMs = options?.timeoutMs ?? 4500;
    const notification: Notification = { id, message, variant, timeoutMs };
    setNotifications(prev => [notification, ...prev]);
    if (timeoutMs && timeoutMs > 0) {
      window.setTimeout(() => remove(id), timeoutMs);
    }
  }, [remove]);

  return (
    <NotificationContext.Provider value={{ notify, remove, notifications }}>
      {children}
    </NotificationContext.Provider>
  );
};




